package com.schoolconnect.app.feature.mystudents

import com.schoolconnect.app.feature.common.PaginationPolicy
import com.schoolconnect.app.feature.common.ServerSort
import com.schoolconnect.app.feature.common.ServerSortOrder
import com.schoolconnect.app.designsystem.appDateInputToApi
import com.schoolconnect.app.feature.students.StudentRowUi

data class MyStudentsFilterValues(
    val className: String = "",
    val classSectionId: String = "",
    val gender: String = ""
)

data class MyStudentRowUi(
    val key: String,
    val studentId: String? = null,
    val rollNumber: String,
    val studentName: String,
    val classLabel: String,
    val gender: String,
    val attendancePercentage: Double? = null,
    val examGrade: String,
    val examSubject: String,
    val subjectCodes: List<String>
) {
    val performance: String get() = when {
        attendancePercentage == null -> "—"
        attendancePercentage >= 75.0 -> "Good Performance"
        else -> "Needs Attention"
    }
}

data class TeacherStudentExamResultUi(
    val examId: String,
    val subjectId: String,
    val marks: String,
    val internalScore: String,
    val mainScore: String,
    val grade: String,
    val rank: String,
    val status: String,
    val remarks: String
)

data class MyStudentsClassOption(val id: String, val label: String)

data class MyStudentsUiState(
    val isLoading: Boolean = false,
    val errorMessage: String = "",
    val searchQuery: String = "",
    val filters: MyStudentsFilterValues = MyStudentsFilterValues(),
    val rows: List<MyStudentRowUi> = emptyList(),
    val totalCount: Int = 0,
    val classOptions: List<MyStudentsClassOption> = emptyList(),
    val subjectOptions: List<String> = emptyList(),
    val pageIndex: Int = 0,
    val pageSize: Int = PaginationPolicy.DEFAULT_PAGE_SIZE,
    val activeAcademicYearId: String = "",
    val dashboardTotalStudents: Int? = null,
    val dashboardAssignedClasses: Int? = null,
    val dashboardGoodPerformance: Int? = null,
    val dashboardNeedsAttention: Int? = null,
    val studentDetail: StudentRowUi? = null,
    val isStudentDetailLoading: Boolean = false,
    val studentDetailError: String = "",
    val studentExamResults: List<TeacherStudentExamResultUi> = emptyList(),
    val isStudentExamResultsLoading: Boolean = false,
    val studentExamResultsError: String = "",
    val sortField: MyStudentsSortField = MyStudentsSortField.ROLL_NUMBER,
    val sortAscending: Boolean = true
) {
    val totalStudents: Int get() = dashboardTotalStudents ?: totalCount
    val assignedClasses: Int get() = dashboardAssignedClasses ?: classOptions.size
    val goodPerformance: Int? get() = dashboardGoodPerformance
    val needsAttention: Int? get() = dashboardNeedsAttention
}

sealed interface MyStudentsUiEvent {
    data object RefreshRequested : MyStudentsUiEvent
    data class SearchChanged(val query: String) : MyStudentsUiEvent
    data class FiltersChanged(val filters: MyStudentsFilterValues) : MyStudentsUiEvent
    data object FiltersReset : MyStudentsUiEvent
    data class PageChanged(val pageIndex: Int) : MyStudentsUiEvent
    data class PageSizeChanged(val pageSize: Int) : MyStudentsUiEvent
    data object ClearError : MyStudentsUiEvent
    data class ViewStudentRequested(val studentId: String) : MyStudentsUiEvent
    data object StudentDetailDismissed : MyStudentsUiEvent
    data class ViewExamResultsRequested(val studentId: String) : MyStudentsUiEvent
    data object StudentExamResultsDismissed : MyStudentsUiEvent
    data class SortChanged(val field: MyStudentsSortField) : MyStudentsUiEvent
}

/** Sort keys accepted by GET /api/v1/school-staff/dashboard/students. */
enum class MyStudentsSortField(val apiField: String) {
    ROLL_NUMBER("roll_number"),
    STUDENT_NAME("name"),
    CLASS("class_name"),
    GENDER("gender"),
    ATTENDANCE("attendance_percentage"),
    PERFORMANCE("average_exam_grade")
}

internal fun MyStudentsSortField.toServerSort(ascending: Boolean): ServerSort = ServerSort(
    field = apiField,
    order = if (ascending) ServerSortOrder.ASC else ServerSortOrder.DESC
)

internal fun normalizeAttendanceDateFilter(value: String): String? =
    appDateInputToApi(value).orEmpty().ifBlank { null }
